/**
 * Light-DOM modal portal for widgets that opt into `mode="modal"`.
 *
 * The dialog lives in the document body (Light DOM), so:
 *  - The host site can style/override it if needed
 *  - The widget can still render its React content with full CSS isolation by
 *    creating its own Shadow Root on a child element inside `container`.
 *
 * Note: that inner Shadow Root has the same constraint as the inline mode —
 * Stripe Elements never becomes ready inside it. The PaymentElement is projected
 * back into Light DOM (a `[slot]` child of the shadow host) by `LightDomSlot`.
 *
 * Plein écran sur petit écran (spec Parcours § 8) : voir `FULLSCREEN_STYLE`. Desktop inchangé.
 */

export interface ModalPortalOptions {
  /** Where to mount the dialog (typically `document`). Defaults to `document`. */
  documentRoot?: Document;
  /** Called when the modal is closed (Escape, backdrop click, close button, or programmatic). */
  onClose?: () => void;
  /** Optional aria-label for accessibility. */
  ariaLabel?: string;
  /** Libellé accessible du bouton « fermer », affiché seulement en plein écran (petit écran). */
  closeLabel?: string;
}

export interface ModalPortalHandle {
  /** The dialog element itself (Light DOM). */
  dialog: HTMLDialogElement;
  /** Inner container where the widget should mount its content. */
  container: HTMLDivElement;
  /** Whether the modal is currently open. */
  readonly isOpen: boolean;
  /** Open the modal. */
  open: () => void;
  /** Close the modal. */
  close: () => void;
  /** Permanently remove the modal from the DOM. */
  destroy: () => void;
}

/**
 * Attribut à poser sur le nœud que le widget monte dans `container` (l'hôte de son shadow root) : en plein
 * écran, c'est lui qui défile, sous la barre du bouton « fermer ».
 */
export const MODAL_CONTENT_ATTRIBUTE = 'data-propelsia-widget-content';

const DIALOG_STYLE =
  'border:none;padding:0;background:transparent;max-width:100vw;max-height:100vh;width:100%;height:100%;inset:0;';
const BACKDROP_STYLE = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:1;cursor:pointer;';
// Fond du thème (`bg` du widget, posé sur la dialog) : le tunnel est transparent, un thème sombre donnait
// sinon un texte clair sur blanc. Sans attribut `bg`, blanc comme avant.
const CONTAINER_STYLE =
  'position:relative;margin:auto;background:var(--w-bg,#fff);border-radius:12px;max-width:900px;width:calc(100% - 32px);max-height:calc(100vh - 32px);overflow:auto;z-index:2;box-shadow:0 20px 60px rgba(0,0,0,0.3);';
// Barre du bouton « fermer » : masquée sur desktop (le voile autour de la modale la ferme), affichée en
// plein écran, où il n'y a plus de voile à toucher.
const TOOLBAR_STYLE = 'display:none;flex:0 0 auto;justify-content:flex-end;margin:0;padding:4px 4px 0;';
// Bouton en Light DOM : ses propriétés sont posées en ligne pour primer sur les styles `button` du site hôte.
const CLOSE_BUTTON_STYLE =
  'box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;min-width:0;min-height:0;margin:0;padding:0;border:0;border-radius:50%;background:transparent;box-shadow:none;color:var(--w-text,#1a1a1a);cursor:pointer;';

/*
 * Plein écran sous ce seuil (téléphone, ou paysage peu haut) : pleine largeur et pleine hauteur en `dvh`
 * (barres du navigateur mobile ; repli 100 %, la dialog modale étant fixe), sans arrondi ni voile, zones
 * sûres (encoche, barre d'accueil) en padding de la dialog peint du fond du thème. Le contenu y devient le
 * conteneur de défilement, sous la barre du bouton « fermer ». Les styles de base sont en ligne (prioritaires
 * sur les feuilles du site hôte) : ces règles les surchargent donc en !important. Au-delà du seuil, rien ne
 * change.
 */
const FULLSCREEN_MEDIA = '(max-width: 640px), (max-height: 500px)';
const FULLSCREEN_STYLE = `@media ${FULLSCREEN_MEDIA} {
  [data-propelsia-widget-modal] {
    width: 100% !important;
    max-width: none !important;
    height: 100% !important;
    height: 100dvh !important;
    max-height: none !important;
    margin: 0 !important;
    padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px) !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    background: var(--w-bg, #fff) !important;
  }
  [data-propelsia-widget-backdrop] {
    display: none !important;
  }
  [data-propelsia-widget-container] {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    max-width: none !important;
    height: 100% !important;
    max-height: none !important;
    margin: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    overflow: hidden !important;
  }
  [data-propelsia-widget-toolbar] {
    display: flex !important;
  }
  [${MODAL_CONTENT_ATTRIBUTE}] {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    overflow: auto !important;
    overscroll-behavior: contain;
  }
}`;

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Croix du bouton « fermer » (SVG construit nœud par nœud : pas d'innerHTML, bloqué par Trusted Types). */
function createCloseIcon(root: Document): SVGSVGElement {
  const svg = root.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.style.cssText = 'display:block;width:24px;height:24px;';
  const path = root.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M6 6l12 12M18 6L6 18');
  svg.appendChild(path);
  return svg;
}

export function createModalPortal(opts: ModalPortalOptions = {}): ModalPortalHandle {
  const root = opts.documentRoot ?? document;
  const dialog = root.createElement('dialog');
  dialog.style.cssText = DIALOG_STYLE;
  dialog.setAttribute('data-propelsia-widget-modal', '');
  if (opts.ariaLabel) dialog.setAttribute('aria-label', opts.ariaLabel);

  const fullscreenStyle = root.createElement('style');
  fullscreenStyle.textContent = FULLSCREEN_STYLE;

  const backdrop = root.createElement('div');
  backdrop.style.cssText = BACKDROP_STYLE;
  backdrop.setAttribute('data-propelsia-widget-backdrop', '');

  const container = root.createElement('div');
  container.style.cssText = CONTAINER_STYLE;
  container.setAttribute('data-propelsia-widget-container', '');

  const toolbar = root.createElement('div');
  toolbar.style.cssText = TOOLBAR_STYLE;
  toolbar.setAttribute('data-propelsia-widget-toolbar', '');
  const closeButton = root.createElement('button');
  closeButton.type = 'button';
  closeButton.style.cssText = CLOSE_BUTTON_STYLE;
  closeButton.setAttribute('data-propelsia-widget-close', '');
  if (opts.closeLabel) closeButton.setAttribute('aria-label', opts.closeLabel);
  closeButton.appendChild(createCloseIcon(root));
  toolbar.appendChild(closeButton);
  container.appendChild(toolbar);

  dialog.appendChild(fullscreenStyle);
  dialog.appendChild(backdrop);
  dialog.appendChild(container);

  let isOpen = false;

  const handle: ModalPortalHandle = {
    dialog,
    container,
    get isOpen(): boolean {
      return isOpen;
    },
    open: (): void => {
      if (!dialog.isConnected) (root.body ?? root.documentElement).appendChild(dialog);
      if (!isOpen) {
        dialog.showModal();
        isOpen = true;
      }
    },
    close: (): void => {
      if (isOpen) {
        dialog.close();
        isOpen = false;
        opts.onClose?.();
      }
    },
    destroy: (): void => {
      if (isOpen) {
        dialog.close();
        isOpen = false;
      }
      if (dialog.isConnected) dialog.remove();
    },
  };

  backdrop.addEventListener('click', (): void => handle.close());
  closeButton.addEventListener('click', (): void => handle.close());
  dialog.addEventListener('cancel', (event): void => {
    event.preventDefault();
    handle.close();
  });

  return handle;
}
